import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { onDocumentLoaded } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * @param {CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    // CartAddEvent shares its event name with ThemeEvents.cartUpdate (see
    // assets/events.js), so this handler also fires on every cart mutation
    // (quantity change, remove) — re-run the preload in case new line-item
    // images were just morphed into a still-closed drawer.
    this.#preloadImages();

    // A failed add keeps the shopper on the page so they can read the error,
    // which the modal drawer would otherwise cover.
    if (event.detail?.data?.openCartDrawer === false || event.detail?.data?.didError) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Preload images that are set to load lazily — the drawer's native
   * <dialog> is display:none while closed, so the browser won't fetch
   * loading="lazy" images inside it until the dialog opens. Mirrors
   * header-menu.js's #preloadImages, which fixes the same class of bug
   * for the (also initially-hidden) mega menu.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  open() {
    this.#preloadImages();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
